/**
 * Parts management - dedicated tab for the generic `part` Tier 2 entity.
 *
 * Parts are reference data ("what was installed" / "what a product is made of"),
 * not warrantable products. The entity key is generic; the per-tenant
 * EntityType.label carries the tenant's word (OC labels parts "Components"), so
 * this page reads its heading + button copy from `entity.label` - the
 * warranty/guarantee translation pattern, no hardcoded tenant strings.
 *
 * Composes the shared entity-record CRUD hooks + the generic record column
 * builder + EditFormDialog (generated form from the entity's own attributes).
 * No bespoke picker, no hand-rolled form.
 */

import { useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { DataTable } from '@/admin/components/data-table'
import type { DataTableColumn } from '@/admin/components/data-table'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import {
  useEntityTypeByKey,
  useEntityRecords,
  useEntityRecordColumns,
  useCreateEntityRecord,
  useUpdateEntityRecord,
  useDeleteEntityRecord,
  type EntityRecordProjection,
} from '@/admin/hooks/use-entities'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { normaliseRegistrationField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

const PART_ENTITY_KEY = 'part'

export function PartsPage() {
  const { data: entity, isLoading: entityLoading } =
    useEntityTypeByKey(PART_ENTITY_KEY)
  const label = entity?.label || 'Part'
  const labelPlural = `${label}s`

  const [search, setSearch] = useState('')
  // Paged like every other list: it used to load the first 100 and stop.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const { data: recordsResponse, isLoading: recordsLoading } = useEntityRecords(
    entity?.uuid,
    { search: search || undefined, page, page_size: pageSize },
  )

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<EntityRecordProjection | null>(null)
  const [deleting, setDeleting] = useState<EntityRecordProjection | null>(null)

  const createRecord = useCreateEntityRecord(entity?.uuid)
  const updateRecord = useUpdateEntityRecord(entity?.uuid)
  const deleteRecord = useDeleteEntityRecord(entity?.uuid)

  const baseColumns = useEntityRecordColumns(entity)
  const columns = useMemo<DataTableColumn<EntityRecordProjection>[]>(() => {
    const actions: DataTableColumn<EntityRecordProjection> = {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Delete ${row.label || labelPlural}`}
            onClick={(e) => {
              e.stopPropagation()
              setDeleting(row)
            }}
            className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
    }
    return [...baseColumns, actions]
  }, [baseColumns, labelPlural])

  const fields = useMemo(
    () => (entity?.attributes ?? []).map((attr) => normaliseRegistrationField(attr)),
    [entity?.attributes],
  )

  const records = recordsResponse?.results ?? []
  const dialogOpen = creating || editing !== null

  async function handleSave(values: Record<string, unknown>) {
    const recordLabel = String(values.name ?? values.product_line ?? '').trim()
    try {
      if (editing) {
        await updateRecord.mutateAsync({
          recordUuid: editing.record_uuid,
          values,
          label: recordLabel,
        })
        toast.success(`${label} updated`)
      } else {
        await createRecord.mutateAsync({ values, label: recordLabel })
        toast.success(`${label} created`)
      }
      setCreating(false)
      setEditing(null)
    } catch (err) {
      toastApiError(err, `Failed to save ${label.toLowerCase()}`)
      throw err
    }
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await deleteRecord.mutateAsync(deleting.record_uuid)
      toast.success(`${label} deleted`)
      setDeleting(null)
    } catch (err) {
      toastApiError(err, `Failed to delete ${label.toLowerCase()}`)
    }
  }

  if (!entityLoading && !entity) {
    return (
      <div className="py-2">
        <p className="text-[13px] text-[var(--ink-3)]">
          Parts are not configured for this tenant. They are a tenant-defined
          entity - add the <code>part</code> entity to manage them here.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <span className="clara-section-label">{labelPlural}</span>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder={`Search ${labelPlural.toLowerCase()}…`}
            className="h-8 w-56"
          />
          <Button
            data-testid="parts-add-btn"
            onClick={() => setCreating(true)}
            className="flex cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add {label.toLowerCase()}
          </Button>
        </div>
      </div>

      <DataTable<EntityRecordProjection>
        tableId="parts-records"
        columns={columns}
        data={records}
        isLoading={entityLoading || recordsLoading}
        rowKey="record_uuid"
        onRowClick={(row) => setEditing(row)}
        emptyMessage={`No ${labelPlural.toLowerCase()} yet - add one to get started.`}
        rowTestId={(row) => `parts-row-${row.record_uuid}`}
        page={page}
        pageSize={pageSize}
        totalCount={recordsResponse?.count ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <EditFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false)
            setEditing(null)
          }
        }}
        title={editing ? `Edit ${label.toLowerCase()}` : `Add ${label.toLowerCase()}`}
        fields={fields}
        values={editing?.values ?? {}}
        onSave={handleSave}
        isPending={createRecord.isPending || updateRecord.isPending}
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={`Delete ${deleting?.label || label.toLowerCase()}?`}
        description={
          <>
            Delete <strong>{deleting?.label}</strong>? This action cannot be undone.
          </>
        }
        onConfirm={handleDelete}
        isPending={deleteRecord.isPending}
      />
    </div>
  )
}
